import { Alert, Box, CircularProgress, DialogContentText, Link, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { FC } from 'react';
import { Link as RouterLink } from 'react-router';

import { toast } from 'sonner';
import { useUserPermissions } from 'src/hooks/useUserPermissions';
import { useUpdatePiecesMutation } from 'src/redux/api/piece';
import { useCountConflictsByPieceMutation } from 'src/redux/api/pieceAutoActions';
import { administrationRoutes } from 'src/routes/paths';
import { PieceAction } from 'src/types/apiContract';
import { Roles } from 'src/types/shared';
import { DialogConfirmation } from 'src/uiKit/DialogConfirmation';

import { showApiErrorToast } from '@utils/apiErrorToast';

import { countSx, progressSx } from './styles';

type Props = {
  onClose: () => void;
  isLoading?: boolean;
  pieces?: number[];
  isSendAll?: boolean;
};

type ContentProps = {
  isLoading: boolean;
  conflictsCount?: number;
  piecesCount: number;
  isSendAll?: boolean;
};

const linkSx = (theme: Theme) => ({ color: theme.palette.warning.darker });

const getAlertMessage = (piecesCount: number, isAdmin: boolean) => {
  switch (true) {
    case piecesCount === 1 && isAdmin:
      return (
        <>
          Automations for this recipient are in conflict and won't be applied.{' '}
          <Link underline="always" sx={linkSx} component={RouterLink} to={administrationRoutes.automationsConflicts}>
            Click here to resolve conflicts.
          </Link>
        </>
      );

    case piecesCount > 1 && isAdmin:
      return (
        <>
          Automations for some recipients are in conflict and won't be applied.{' '}
          <Link underline="always" sx={linkSx} component={RouterLink} to={administrationRoutes.automationsConflicts}>
            Click here to resolve conflicts.
          </Link>
        </>
      );

    case piecesCount === 1 && !isAdmin:
      return "Automations for this recipient are in conflict and won't be applied. Contact your administrator to resolve these conflicts.";

    case piecesCount > 1 && !isAdmin:
      return "Automations for some recipients are in conflict and won't be applied. Contact your administrator to resolve these conflicts.";

    default:
      return '';
  }
};

const getTitle = (length: number, isSendAll = false) => {
  if (isSendAll) return 'Send all assigned items';

  if (length > 1) {
    return 'Send selected items';
  }

  return 'Send item';
};

const getMainText = (length: number, isSendAll = false) => {
  if (isSendAll)
    return 'Are you sure you would like to send all assigned items to recipients? Note that any unassigned items will not be sent.';
  if (length > 1)
    return (
      <>
        Are you sure you would like to send{' '}
        <Typography variant="body1" sx={countSx}>
          {length}
        </Typography>{' '}
        item(s) to recipients?
      </>
    );
  return 'Are you sure you would like to send this item to the recipient?';
};

const SendItemContent: FC<ContentProps> = ({ isLoading, conflictsCount, piecesCount, isSendAll = false }) => {
  const { isAllowed } = useUserPermissions();

  const isAdmin = isAllowed([Roles.Admin]);

  if (isLoading) {
    return (
      <Box sx={progressSx}>
        <CircularProgress aria-label="Loading..." />
      </Box>
    );
  }

  return (
    <>
      {conflictsCount && conflictsCount > 0 ? (
        <Alert variant="standard" severity="warning" sx={{ mb: 3 }} color="warning">
          {getAlertMessage(piecesCount, isAdmin)}
        </Alert>
      ) : null}
      <DialogContentText>{getMainText(piecesCount, isSendAll)}</DialogContentText>
    </>
  );
};

const SendItems: FC<Props> = ({ onClose, isSendAll = false, isLoading = false, pieces = [] }) => {
  const [conflictsByPiece, { isLoading: conflictCountLoading, data }] = useCountConflictsByPieceMutation();
  const [updatePieces, { isLoading: piecesUpdating }] = useUpdatePiecesMutation();

  if (data === undefined && !conflictCountLoading) {
    conflictsByPiece(pieces);
  }

  const handleSendAll = async () => {
    try {
      await updatePieces({
        pieces: pieces.map((pieceId) => ({ id: pieceId })),
        action: PieceAction.Submit,
      }).unwrap();

      toast.success('All items sent successfully', { duration: 3000 });
      onClose();
    } catch (error) {
      showApiErrorToast(error);
    }
  };

  return (
    <DialogConfirmation
      open
      maxWidth="xs"
      title={getTitle(pieces.length, isSendAll)}
      content={
        <SendItemContent
          conflictsCount={data}
          isLoading={isLoading}
          piecesCount={pieces.length}
          isSendAll={isSendAll}
        />
      }
      acceptButtonHandleClick={handleSendAll}
      cancelButtonHandleClick={onClose}
      acceptButtonTitle="Send"
      cancelButtonTitle="Cancel"
      loading={conflictCountLoading || piecesUpdating}
    />
  );
};

export default SendItems;
